import { useGetFormValidationError } from "@tm/localization"
import { ensureNumber } from "@tm/utils"
import { useMemo } from "react"
import { z } from "zod"

const MIN_POSSIBLE_NUMBER = 0.01
const MAX_POSSIBLE_NUMBER = 99999.99

export function useSchema(hasFixedPrice: boolean, workNumberRequired: boolean) {
    const getError = useGetFormValidationError()
    const rangeError = getError("VALUE_RANGE", MIN_POSSIBLE_NUMBER, MAX_POSSIBLE_NUMBER)
    const minPossibleNumberDependingOnFixedPrice = hasFixedPrice ? 0 : MIN_POSSIBLE_NUMBER
    const rangeErrorDependingOnFixedPrice = getError("VALUE_RANGE", minPossibleNumberDependingOnFixedPrice, MAX_POSSIBLE_NUMBER)

    const baseSchema = z.object({
        saveAsOwnWork: z.boolean().default(false),

        workNumber: z.string().optional(),
        description: z.string().trim().min(1, getError("REQUIRED")),
        category: z.number().gt(0, getError("REQUIRED")),
        rebate: ensureNumber(z.number({ required_error: getError("REQUIRED") }).gte(0)),
        vatType: z.number().gt(0, getError("REQUIRED")),

        surcharge: ensureNumber(z.number().gte(0)).optional(),
        time: ensureNumber(
            z
                .number()
                .min(minPossibleNumberDependingOnFixedPrice, rangeErrorDependingOnFixedPrice)
                .max(MAX_POSSIBLE_NUMBER, rangeErrorDependingOnFixedPrice)
        ).optional(),
        hourlyRate: ensureNumber(
            z.number().min(minPossibleNumberDependingOnFixedPrice, rangeErrorDependingOnFixedPrice).max(MAX_POSSIBLE_NUMBER, rangeError)
        ).optional(),
        fixedPriceValue: ensureNumber(z.number().min(MIN_POSSIBLE_NUMBER, rangeError).max(MAX_POSSIBLE_NUMBER, rangeError)).optional(),

        customRepairTimeListId: z.string().trim().min(1).optional(),
    })

    return useMemo(() => {
        return baseSchema.superRefine((data, ctx) => {
            if (workNumberRequired && !data.workNumber) {
                ctx.addIssue({
                    path: ["workNumber"],
                    code: z.ZodIssueCode.custom,
                    message: getError("REQUIRED"),
                })
            }

            if (hasFixedPrice) {
                if (data.fixedPriceValue === undefined) {
                    ctx.addIssue({
                        path: ["fixedPriceValue"],
                        code: z.ZodIssueCode.custom,
                        message: getError("REQUIRED"),
                    })
                }
            } else {
                ;(["surcharge", "time", "hourlyRate"] as const).forEach((key) => {
                    if (data[key] === undefined) {
                        ctx.addIssue({
                            path: [key],
                            code: z.ZodIssueCode.custom,
                            message: getError("REQUIRED"),
                        })
                    }
                })
            }

            if (data.saveAsOwnWork && !data.customRepairTimeListId) {
                ctx.addIssue({
                    path: ["customRepairTimeListId"],
                    code: z.ZodIssueCode.custom,
                    message: getError("REQUIRED"),
                })
            }
        })
    }, [baseSchema, getError, hasFixedPrice, workNumberRequired])
}
